/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { artifactFromEvent } from './agentArtifacts';
import {
  applyEventToKnowledgeGraph,
  buildKnowledgeGraphFromHistory,
  createKnowledgeGraph,
  eventsFromConversationTurn,
  startKnowledgeGraphTurn,
} from './components/knowledge-graph/knowledgeGraph';
import {
  dedupeAgentResourceReferences,
  normalizeAgentResourceReferences,
} from './resourceReferences';
import type {
  AgentArtifact,
  AgentConversationHistory,
  AgentConversationTurn,
  AgentEvent,
  AgentEventType,
  AgentKnowledgeGraphState,
  AgentMapView,
  AgentMessage,
  AgentSteerStatus,
  AgentTimelineEvent,
  UploadedFileMeta,
} from './types';

export interface AgentChatState {
  messages: Array<AgentMessage>;
  activeAssistantText: string;
  pendingPlotJson: AgentMessage['plotJson'];
  pendingMapViews: Array<AgentMapView>;
  pendingResourceReferences: NonNullable<AgentMessage['resourceReferences']>;
  currentTurnStartedAtMs?: number;
  // Turn id of the in-flight turn, stamped onto the assistant message when the
  // turn completes so answer feedback can be filed against the exact exchange.
  currentTurnId?: string;
  firstOutputLatencyMs?: number;
  lastEventSequence?: number;
  renderedEventSequences: Array<number>;
  timeline: Array<AgentTimelineEvent>;
  artifacts: Array<AgentArtifact>;
  // Artifacts emitted by the in-flight turn, flushed onto the assistant message
  // when the turn completes (or is steered) so downloads stay attached to the
  // output that produced them.
  currentTurnArtifacts: Array<AgentArtifact>;
  knowledgeGraph: AgentKnowledgeGraphState;
  isRunning: boolean;
  isCancelling: boolean;
  error?: string;
}

export type AgentChatAction =
  | { type: 'load_history'; history: AgentConversationHistory | undefined }
  | {
      type: 'submit_prompt';
      prompt: string;
      startedAtMs?: number;
      attachments?: Array<UploadedFileMeta>;
    }
  | { type: 'submit_steer'; content: string; clientSteerId: string }
  | { type: 'steer_failed'; clientSteerId: string }
  | { type: 'socket_open' }
  | { type: 'cancel' }
  | { type: 'socket_closed' }
  | { type: 'socket_error'; error: string }
  | { type: 'receive_event'; event: AgentEvent; receivedAtMs?: number };

export const initialAgentChatState: AgentChatState = {
  messages: [],
  activeAssistantText: '',
  pendingPlotJson: undefined,
  pendingMapViews: [],
  pendingResourceReferences: [],
  currentTurnStartedAtMs: undefined,
  currentTurnId: undefined,
  firstOutputLatencyMs: undefined,
  lastEventSequence: undefined,
  renderedEventSequences: [],
  timeline: [],
  artifacts: [],
  currentTurnArtifacts: [],
  knowledgeGraph: createKnowledgeGraph(),
  isRunning: false,
  isCancelling: false,
};

const TIMELINE_EVENT_TYPES = new Set<AgentEventType>([
  'thinking_text_chunk',
  'tool_started',
  'tool_finished',
  'skill_activated',
  'kb_read',
  'sandbox_result',
  'artifact_ready',
  'map_ready',
  'context_usage',
]);

const STEER_EVENT_STATUS_BY_TYPE: Partial<Record<AgentEventType, AgentSteerStatus>> = {
  steer_received: 'accepted',
  steer_applied: 'applied',
  steer_expired: 'expired',
};
const MAX_RENDERED_EVENT_SEQUENCES = 1_000;

export const agentChatReducer = (
  state: AgentChatState,
  action: AgentChatAction,
): AgentChatState => {
  switch (action.type) {
    case 'load_history':
      return loadHistoryState(state, action.history);
    case 'submit_prompt': {
      const submittedAt = action.startedAtMs ?? Date.now();
      return {
        ...state,
        error: undefined,
        activeAssistantText: '',
        pendingPlotJson: undefined,
        pendingMapViews: [],
        pendingResourceReferences: [],
        currentTurnStartedAtMs: submittedAt,
        currentTurnId: undefined,
        firstOutputLatencyMs: undefined,
        timeline: [],
        currentTurnArtifacts: [],
        knowledgeGraph: startKnowledgeGraphTurn(state.knowledgeGraph, action.prompt),
        isRunning: true,
        isCancelling: false,
        messages: [
          ...state.messages,
          {
            id: crypto.randomUUID(),
            type: 'user',
            content: action.prompt,
            createdAt: new Date(submittedAt).toISOString(),
            attachments: action.attachments,
          },
        ],
      };
    }
    case 'submit_steer':
      return appendSteerMessage(state, action.content, action.clientSteerId);
    case 'steer_failed':
      return updateSteerMessage(state, action.clientSteerId, { status: 'failed' });
    case 'socket_open':
      return { ...state, isRunning: true, isCancelling: false, error: undefined };
    case 'cancel':
      return { ...state, isCancelling: true };
    case 'socket_closed':
      return completeActiveAssistant(state, { isRunning: false, isCancelling: false });
    case 'socket_error':
      return {
        ...state,
        isRunning: false,
        isCancelling: false,
        error: action.error,
        knowledgeGraph: applyEventToKnowledgeGraph(state.knowledgeGraph, {
          type: 'error',
          content: action.error,
        }),
      };
    case 'receive_event':
      return applyAgentEvent(state, action.event, action.receivedAtMs);
    default:
      return state;
  }
};

const loadHistoryState = (
  state: AgentChatState,
  history: AgentConversationHistory | undefined,
): AgentChatState => {
  const latestTurn = history?.turns.at(-1);
  const latestTurnEvents = eventsFromTurn(latestTurn);
  // Track sequences across every turn, not just the latest, so a backend
  // replay (subscribe with no after_sequence) is fully deduped instead of
  // re-rendering earlier turns from the beginning of the conversation.
  const allHistoryEvents = (history?.turns ?? []).flatMap(eventsFromTurn);
  const historyLastEventSequence = lastEventSequence(allHistoryEvents);
  const messages = historyToMessages(history);
  if (
    history &&
    state.lastEventSequence !== undefined &&
    (historyLastEventSequence ?? -1) < state.lastEventSequence &&
    hasLocalPromptMissingFromHistory(state.messages, messages)
  ) {
    return state;
  }
  const isRunning = isRunningTurn(latestTurn, latestTurnEvents);
  return {
    ...initialAgentChatState,
    messages: mergeLocalTurnOverlayMessages(messages, state.messages),
    activeAssistantText: isRunning ? assistantTextFromEvents(latestTurnEvents) : '',
    pendingPlotJson: isRunning ? plotJsonFromEvents(latestTurnEvents) : undefined,
    pendingMapViews: isRunning ? mapViewsFromEvents(latestTurnEvents) : [],
    currentTurnStartedAtMs: isRunning ? turnStartedAtMs(latestTurn, latestTurnEvents) : undefined,
    currentTurnId: isRunning && latestTurn ? turnIdOf(latestTurn) : undefined,
    firstOutputLatencyMs: firstOutputLatencyFromTurn(latestTurn, latestTurnEvents),
    lastEventSequence: historyLastEventSequence,
    renderedEventSequences: boundedEventSequences(eventSequences(allHistoryEvents)),
    timeline: historyToTimeline(history),
    artifacts: artifactsFromEvents(allHistoryEvents),
    currentTurnArtifacts: isRunning ? artifactsFromEvents(latestTurnEvents) : [],
    knowledgeGraph: buildKnowledgeGraphFromHistory(history),
    isRunning,
  };
};

const applyAgentEvent = (
  state: AgentChatState,
  event: AgentEvent,
  receivedAtMs?: number,
): AgentChatState => {
  if (isDuplicateEvent(state, event) || (event.type === 'map_ready' && !event.map_view)) {
    return state;
  }
  const sequencedState = markEventRendered(state, event);
  const knowledgeGraph = applyEventToKnowledgeGraph(state.knowledgeGraph, event);
  const steerStatus = STEER_EVENT_STATUS_BY_TYPE[event.type];
  if (steerStatus) {
    return applySteerStatusEvent(sequencedState, event, steerStatus, receivedAtMs);
  }

  switch (event.type) {
    case 'turn_started':
      return {
        ...sequencedState,
        knowledgeGraph,
        currentTurnId: event.turn_id ?? undefined,
        currentTurnStartedAtMs: eventStartedAtMs(event, receivedAtMs),
        firstOutputLatencyMs: undefined,
        pendingMapViews: [],
        currentTurnArtifacts: [],
        isRunning: true,
        isCancelling: false,
        error: undefined,
      };
    case 'assistant_text_chunk':
      return {
        ...sequencedState,
        knowledgeGraph,
        firstOutputLatencyMs: firstOutputLatencyForEvent(sequencedState, event, receivedAtMs),
        activeAssistantText: `${sequencedState.activeAssistantText}${event.content ?? ''}`,
      };
    case 'resource_references': {
      const resourceReferences = normalizeAgentResourceReferences(event.references);
      logAgentResourceReferencesReceived(event, resourceReferences);
      return {
        ...sequencedState,
        knowledgeGraph,
        pendingResourceReferences: dedupeAgentResourceReferences([
          ...sequencedState.pendingResourceReferences,
          ...resourceReferences,
        ]),
      };
    }
    case 'thinking_text_chunk':
      return {
        ...sequencedState,
        knowledgeGraph,
        timeline: appendThinkingTimelineEvent(sequencedState.timeline, event),
      };
    case 'done':
      return completeActiveAssistant(
        { ...sequencedState, knowledgeGraph },
        { isRunning: false, isCancelling: false },
      );
    case 'cancelled':
      return completeActiveAssistant(
        { ...sequencedState, knowledgeGraph },
        { isRunning: false, isCancelling: false },
      );
    case 'error':
      return applyErrorEvent(sequencedState, event, knowledgeGraph);
    case 'artifact_ready': {
      const artifact = artifactFromEvent(event);
      return {
        ...sequencedState,
        knowledgeGraph,
        artifacts: [...sequencedState.artifacts, artifact],
        currentTurnArtifacts: [...sequencedState.currentTurnArtifacts, artifact],
        timeline: [
          ...sequencedState.timeline,
          eventToTimeline(event, sequencedState.timeline.length),
        ],
      };
    }
    case 'map_ready':
      return {
        ...sequencedState,
        knowledgeGraph,
        pendingMapViews: [...sequencedState.pendingMapViews, event.map_view as AgentMapView],
        timeline: [
          ...sequencedState.timeline,
          eventToTimeline(event, sequencedState.timeline.length),
        ],
      };
    case 'tool_started':
    case 'skill_activated':
    case 'kb_read':
    case 'context_usage':
      return {
        ...sequencedState,
        knowledgeGraph,
        timeline: [
          ...sequencedState.timeline,
          eventToTimeline(event, sequencedState.timeline.length),
        ],
      };
    case 'tool_finished':
    case 'sandbox_result':
      return {
        ...sequencedState,
        knowledgeGraph,
        pendingPlotJson: event.plot_json ?? sequencedState.pendingPlotJson,
        timeline: [
          ...sequencedState.timeline,
          eventToTimeline(event, sequencedState.timeline.length),
        ],
      };
    default:
      return sequencedState;
  }
};

const isDuplicateEvent = (state: AgentChatState, event: AgentEvent) => {
  if (typeof event.sequence !== 'number') return false;
  if (
    state.lastEventSequence !== undefined &&
    event.sequence <= state.lastEventSequence - MAX_RENDERED_EVENT_SEQUENCES
  ) {
    return true;
  }
  return state.renderedEventSequences.includes(event.sequence);
};

const markEventRendered = (state: AgentChatState, event: AgentEvent): AgentChatState => {
  if (typeof event.sequence !== 'number') return state;
  return {
    ...state,
    lastEventSequence:
      state.lastEventSequence === undefined
        ? event.sequence
        : Math.max(state.lastEventSequence, event.sequence),
    renderedEventSequences: boundedEventSequences([
      ...state.renderedEventSequences,
      event.sequence,
    ]),
  };
};

const boundedEventSequences = (sequences: Array<number>) =>
  sequences.slice(-MAX_RENDERED_EVENT_SEQUENCES);

const firstOutputLatencyForEvent = (
  state: AgentChatState,
  event: AgentEvent,
  receivedAtMs?: number,
) => {
  if (state.firstOutputLatencyMs !== undefined || !state.currentTurnStartedAtMs) {
    return state.firstOutputLatencyMs;
  }
  if (event.type === 'assistant_text_chunk' && !event.content) {
    return state.firstOutputLatencyMs;
  }
  return Math.max(0, (receivedAtMs ?? Date.now()) - state.currentTurnStartedAtMs);
};

const eventStartedAtMs = (event: AgentEvent, receivedAtMs?: number) => {
  if (!event.created_at) return receivedAtMs ?? Date.now();
  const parsed = Date.parse(event.created_at);
  return Number.isFinite(parsed) ? parsed : (receivedAtMs ?? Date.now());
};

const firstOutputLatencyFromTurn = (
  turn: AgentConversationTurn | undefined,
  events = eventsFromTurn(turn),
) => {
  if (!turn?.output_start_at || !turn.first_output_at) return firstOutputLatencyFromEvents(events);
  const outputStartAt = Date.parse(turn.output_start_at);
  const firstOutputAt = Date.parse(turn.first_output_at);
  if (!Number.isFinite(outputStartAt) || !Number.isFinite(firstOutputAt)) {
    return firstOutputLatencyFromEvents(events);
  }
  return Math.max(0, firstOutputAt - outputStartAt);
};

const applySteerStatusEvent = (
  state: AgentChatState,
  event: AgentEvent,
  status: AgentSteerStatus,
  receivedAtMs?: number,
): AgentChatState => {
  if (!event.client_steer_id) return state;
  const nextState = updateSteerMessage(state, event.client_steer_id, {
    status,
    steerId: event.steer_id ?? undefined,
  });
  return status === 'applied'
    ? {
        ...nextState,
        currentTurnStartedAtMs: receivedAtMs ?? Date.now(),
        firstOutputLatencyMs: undefined,
      }
    : nextState;
};

const applyErrorEvent = (
  state: AgentChatState,
  event: AgentEvent,
  knowledgeGraph: AgentKnowledgeGraphState,
): AgentChatState => {
  const errorContent = event.content ?? 'The agent encountered an error.';
  if (event.client_steer_id) {
    const nextState = updateSteerMessage(state, event.client_steer_id, { status: 'failed' });
    if (event.recoverable) {
      return { ...nextState, knowledgeGraph, isCancelling: false };
    }
    // Non-recoverable: terminate the turn like done/cancelled — finalize any
    // streamed text, clear the working state, and surface the error.
    return completeActiveAssistant(
      { ...nextState, knowledgeGraph, error: errorContent },
      { isRunning: false, isCancelling: false },
    );
  }
  if (event.recoverable) {
    return { ...state, knowledgeGraph, error: errorContent, isRunning: true, isCancelling: false };
  }
  return completeActiveAssistant(
    { ...state, knowledgeGraph, error: errorContent },
    { isRunning: false, isCancelling: false },
  );
};

const appendSteerMessage = (
  state: AgentChatState,
  content: string,
  clientSteerId: string,
): AgentChatState => {
  const messages = [...state.messages];
  if (state.timeline.length > 0) {
    messages.push({
      id: crypto.randomUUID(),
      type: 'activity',
      content: '',
      events: state.timeline,
      firstOutputLatencyMs: state.firstOutputLatencyMs,
      createdAt: new Date().toISOString(),
    });
  }
  const assistantContent = state.activeAssistantText.trim();
  if (
    assistantContent ||
    state.pendingPlotJson ||
    state.pendingMapViews.length > 0 ||
    state.pendingResourceReferences.length > 0 ||
    state.currentTurnArtifacts.length > 0
  ) {
    messages.push({
      id: crypto.randomUUID(),
      type: 'assistant',
      content: assistantContent,
      plotJson: state.pendingPlotJson,
      mapViews: state.pendingMapViews.length > 0 ? state.pendingMapViews : undefined,
      resourceReferences: state.pendingResourceReferences,
      artifacts: state.currentTurnArtifacts.length > 0 ? state.currentTurnArtifacts : undefined,
      createdAt: new Date().toISOString(),
    });
  }
  messages.push({
    id: clientSteerId,
    type: 'user',
    content,
    createdAt: new Date().toISOString(),
    steer: {
      clientSteerId,
      status: 'sending',
    },
  });

  return {
    ...state,
    activeAssistantText: '',
    pendingPlotJson: undefined,
    pendingMapViews: [],
    pendingResourceReferences: [],
    timeline: [],
    currentTurnArtifacts: [],
    messages,
  };
};

const updateSteerMessage = (
  state: AgentChatState,
  clientSteerId: string,
  updates: { status: AgentSteerStatus; steerId?: string },
): AgentChatState => ({
  ...state,
  messages: state.messages.map((message) =>
    message.steer?.clientSteerId === clientSteerId
      ? {
          ...message,
          steer: {
            ...message.steer,
            ...updates,
          },
        }
      : message,
  ),
});

const mergeLocalTurnOverlayMessages = (
  historyMessages: Array<AgentMessage>,
  currentMessages: Array<AgentMessage>,
) => {
  const localOverlayMessages = currentMessages.filter(
    (message) => message.type === 'activity' || message.steer,
  );
  if (
    localOverlayMessages.length === 0 ||
    !historyMatchesCurrentTurn(historyMessages, currentMessages)
  ) {
    return historyMessages;
  }

  const latestAssistantIndex = latestMessageIndex(historyMessages, 'assistant');
  const insertionIndex = latestAssistantIndex >= 0 ? latestAssistantIndex : historyMessages.length;
  return [
    ...historyMessages.slice(0, insertionIndex),
    ...localOverlayMessages,
    ...historyMessages.slice(insertionIndex),
  ];
};

const hasLocalPromptMissingFromHistory = (
  currentMessages: Array<AgentMessage>,
  historyMessages: Array<AgentMessage>,
) => {
  const historyMessageIds = new Set(historyMessages.map((message) => message.id));
  return currentMessages.some(
    (message) => message.type === 'user' && !message.steer && !historyMessageIds.has(message.id),
  );
};

const historyMatchesCurrentTurn = (
  historyMessages: Array<AgentMessage>,
  currentMessages: Array<AgentMessage>,
) => {
  const historyUserMessageIds = new Set(
    historyMessages
      .filter((message) => message.type === 'user' && !message.steer)
      .map((message) => message.id),
  );
  const currentLatestAssistantContent = latestAssistantContent(currentMessages);
  return (
    currentMessages.some(
      (currentMessage) =>
        currentMessage.type === 'user' &&
        !currentMessage.steer &&
        historyUserMessageIds.has(currentMessage.id),
    ) ||
    (Boolean(currentLatestAssistantContent) &&
      latestAssistantContent(historyMessages) === currentLatestAssistantContent)
  );
};

const latestAssistantContent = (messages: Array<AgentMessage>) =>
  messages.findLast((message) => message.type === 'assistant')?.content.trim();

const latestMessageIndex = (messages: Array<AgentMessage>, type: AgentMessage['type']) => {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    if (messages[index].type === type) return index;
  }
  return -1;
};

const completeActiveAssistant = (
  state: AgentChatState,
  updates: Pick<AgentChatState, 'isRunning' | 'isCancelling'>,
): AgentChatState => {
  const content = state.activeAssistantText.trim();
  const artifacts = state.currentTurnArtifacts;
  if (
    !content &&
    !state.pendingPlotJson &&
    state.pendingMapViews.length === 0 &&
    state.pendingResourceReferences.length === 0 &&
    artifacts.length === 0
  ) {
    return {
      ...state,
      ...updates,
      activeAssistantText: '',
      pendingMapViews: [],
      pendingResourceReferences: [],
      currentTurnArtifacts: [],
    };
  }

  if (state.pendingResourceReferences.length > 0) {
    // biome-ignore lint/suspicious/noConsole: Intentional turn-level debug signal for agent resource-link wiring.
    console.info('[agent] attaching resource references to assistant turn', {
      count: state.pendingResourceReferences.length,
      references: state.pendingResourceReferences,
    });
  }

  return {
    ...state,
    ...updates,
    activeAssistantText: '',
    pendingPlotJson: undefined,
    pendingMapViews: [],
    pendingResourceReferences: [],
    currentTurnArtifacts: [],
    messages: [
      ...state.messages,
      {
        id: crypto.randomUUID(),
        turnId: state.currentTurnId,
        type: 'assistant',
        content,
        plotJson: state.pendingPlotJson,
        mapViews: state.pendingMapViews.length > 0 ? state.pendingMapViews : undefined,
        resourceReferences: state.pendingResourceReferences,
        artifacts: artifacts.length > 0 ? artifacts : undefined,
        createdAt: new Date().toISOString(),
      },
    ],
  };
};

const logAgentResourceReferencesReceived = (
  event: AgentEvent,
  resourceReferences: NonNullable<AgentMessage['resourceReferences']>,
) => {
  // biome-ignore lint/suspicious/noConsole: Intentional event-level debug signal for agent resource-link wiring.
  console.info('[agent] resource references received', {
    conversationId: event.conversation_id,
    turnId: event.turn_id,
    clientTurnId: event.client_turn_id,
    sequence: event.sequence,
    rawCount: event.references?.length ?? 0,
    linkableCount: resourceReferences.length,
    references: resourceReferences,
    rawReferences: event.references,
  });
};

const eventToTimeline = (event: AgentEvent, sequence: number): AgentTimelineEvent => ({
  id: `${sequence}-${event.type}-${event.tool_name ?? event.artifact_id ?? event.summary ?? crypto.randomUUID()}`,
  type: event.type,
  label: eventLabel(event),
  toolName: event.tool_name,
  detail: eventDetail(event),
  status: event.status,
});

const eventLabel = (event: AgentEvent) => {
  if (event.summary) return event.summary;
  if (event.type === 'thinking_text_chunk') return event.content || 'Thinking';
  if (event.tool_name) return event.tool_name.replaceAll('_', ' ');
  if (event.filename) return event.filename;
  return event.type.replaceAll('_', ' ');
};

const appendThinkingTimelineEvent = (
  timeline: Array<AgentTimelineEvent>,
  event: AgentEvent,
): Array<AgentTimelineEvent> => {
  const previousEvent = timeline.at(-1);
  if (previousEvent?.type !== 'thinking_text_chunk') {
    return [...timeline, eventToTimeline(event, timeline.length)];
  }

  const nextLabel = appendThinkingContent(previousEvent.label, event.content);
  const nextDetail = appendThinkingContent(
    previousEvent.detail ?? previousEvent.label,
    event.content,
  );
  return [
    ...timeline.slice(0, -1),
    {
      ...previousEvent,
      label: nextLabel,
      detail: nextDetail,
    },
  ];
};

const appendThinkingContent = (previousContent: string, nextContent: string | null | undefined) => {
  const content = nextContent ?? '';
  if (!previousContent) return content || 'Thinking';
  if (!content) return previousContent;
  const appendedContent = shouldSeparateThinkingChunk(previousContent, content)
    ? `${previousContent}\n\n${content}`
    : `${previousContent}${content}`;
  return normalizeThinkingMarkdown(appendedContent);
};

const shouldSeparateThinkingChunk = (previousContent: string, nextContent: string) =>
  !hasUnclosedBoldDelimiter(previousContent) &&
  !/\s$/.test(previousContent) &&
  !/^\s/.test(nextContent) &&
  startsMarkdownBlock(nextContent.trimStart());

const startsMarkdownBlock = (content: string) =>
  content.startsWith('**') || /^#{1,6}\s/.test(content) || /^[-*]\s/.test(content);

const hasUnclosedBoldDelimiter = (content: string) =>
  (content.match(/\*\*/g)?.length ?? 0) % 2 === 1;

const normalizeThinkingMarkdown = (content: string) =>
  content.replace(/\*\*([^*\n][^*\n]*?)\n+\*\*/g, '**$1**');

const eventDetail = (event: AgentEvent) => {
  if (event.type === 'sandbox_result') {
    return [event.stdout, event.result_repr].filter(Boolean).join('\n');
  }
  if (event.type === 'context_usage' && event.usage) {
    return JSON.stringify(event.usage);
  }
  return event.content ?? undefined;
};

export const historyToMessages = (
  history: AgentConversationHistory | undefined,
): Array<AgentMessage> =>
  (history?.turns ?? []).flatMap((turn) => {
    const messages: Array<AgentMessage> = [];
    // File-only turns have an empty prompt but must still render the user
    // message so the attachment chips survive a reload.
    if (turn.prompt || turn.attachments?.length) {
      messages.push({
        id: `${turn.conversation_id}-${turn.turn_index}-prompt`,
        type: 'user',
        content: turn.prompt ?? '',
        createdAt: turn.created_at,
        attachments: turn.attachments?.length ? turn.attachments : undefined,
      });
    }

    const assistantMessage = assistantMessageFromTurn(turn);
    if (assistantMessage) messages.push(assistantMessage);

    return messages;
  });

const turnIdOf = (turn: AgentConversationTurn): string | undefined =>
  turn.id ?? turn.turn_id ?? undefined;

const assistantMessageFromTurn = (turn: AgentConversationTurn): AgentMessage | undefined => {
  const turnEvents = eventsFromConversationTurn(turn);
  if (isRunningTurn(turn, turnEvents)) return undefined;

  const assistantText = assistantTextFromTurn(turn) || assistantTextFromEvents(turnEvents);
  const plotJson = plotJsonFromTurn(turn) ?? plotJsonFromEvents(turnEvents);
  const mapViews = mapViewsFromEvents(turnEvents);
  const resourceReferences = normalizeAgentResourceReferences(turn.resource_references);
  const turnArtifacts = artifactsFromEvents(turnEvents);
  if (
    !assistantText &&
    !plotJson &&
    mapViews.length === 0 &&
    resourceReferences.length === 0 &&
    turnArtifacts.length === 0
  ) {
    return undefined;
  }

  return {
    id: `${turn.conversation_id}-${turn.turn_index}-assistant`,
    turnId: turnIdOf(turn),
    feedback: turn.feedback ?? undefined,
    type: 'assistant',
    content: assistantText ?? '',
    plotJson,
    mapViews: mapViews.length > 0 ? mapViews : undefined,
    resourceReferences,
    artifacts: turnArtifacts.length > 0 ? turnArtifacts : undefined,
    createdAt: turn.created_at,
  };
};

export const historyToTimeline = (
  history: AgentConversationHistory | undefined,
): Array<AgentTimelineEvent> => {
  const latestTurn = history?.turns.at(-1);
  if (!latestTurn) return [];
  let timeline: Array<AgentTimelineEvent> = [];
  for (const event of eventsFromTurn(latestTurn)) {
    if (!TIMELINE_EVENT_TYPES.has(event.type)) continue;
    if (event.type === 'thinking_text_chunk') {
      timeline = appendThinkingTimelineEvent(timeline, event);
      continue;
    }
    timeline.push(eventToTimeline(event, timeline.length));
  }
  return timeline;
};

const plotJsonFromTurn = (turn: AgentConversationTurn): AgentMessage['plotJson'] => {
  const candidates: Array<unknown> = [];
  collectPlotJson(turn.result, candidates);
  collectPlotJson(turn.messages, candidates);
  return candidates.find(isPlotJson);
};

const plotJsonFromEvents = (events: Array<AgentEvent>): AgentMessage['plotJson'] =>
  events.map((event) => event.plot_json).find(isPlotJson);

const mapViewsFromEvents = (events: Array<AgentEvent>): Array<AgentMapView> =>
  events
    .filter((event) => event.type === 'map_ready' && event.map_view)
    .map((event) => event.map_view as AgentMapView);

const assistantTextFromTurn = (turn: AgentConversationTurn): string | undefined => {
  const candidates: Array<string> = [];
  collectText(turn.result, candidates);
  collectText(turn.messages, candidates);
  const text = candidates
    .map((candidate) => candidate.trim())
    .filter(Boolean)
    .join('\n\n');
  return text || undefined;
};

const assistantTextFromEvents = (events: Array<AgentEvent>) => {
  const text = events
    .filter((event) => event.type === 'assistant_text_chunk')
    .map((event) => event.content ?? '')
    .join('')
    .trim();
  return text || '';
};

const isRunningTurn = (turn: AgentConversationTurn | undefined, events = eventsFromTurn(turn)) => {
  if (!turn) return false;
  if (events.some((event) => isTerminalHistoryEvent(event))) return false;
  return turn.status === 'running' || turn.status === 'pending' || turn.complete === false;
};

const isTerminalHistoryEvent = (event: AgentEvent) =>
  event.type === 'done' ||
  event.type === 'cancelled' ||
  (event.type === 'error' && !event.recoverable);

const turnStartedAtMs = (turn: AgentConversationTurn | undefined, events: Array<AgentEvent>) => {
  const startedAt =
    events.find((event) => event.type === 'turn_started')?.created_at ??
    turn?.output_start_at ??
    turn?.created_at;
  if (!startedAt) return undefined;
  const parsed = Date.parse(startedAt);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const firstOutputLatencyFromEvents = (events: Array<AgentEvent>) => {
  const startedAt = events.find((event) => event.type === 'turn_started')?.created_at;
  const firstOutputAt = events.find(
    (event) => event.type === 'assistant_text_chunk' && event.content,
  )?.created_at;
  if (!startedAt || !firstOutputAt) return undefined;
  const startedAtMs = Date.parse(startedAt);
  const firstOutputAtMs = Date.parse(firstOutputAt);
  if (!Number.isFinite(startedAtMs) || !Number.isFinite(firstOutputAtMs)) return undefined;
  return Math.max(0, firstOutputAtMs - startedAtMs);
};

const eventSequences = (events: Array<AgentEvent>) =>
  events
    .map((event) => event.sequence)
    .filter((sequence): sequence is number => typeof sequence === 'number');

const lastEventSequence = (events: Array<AgentEvent>) => {
  const sequences = eventSequences(events);
  return sequences.length ? Math.max(...sequences) : undefined;
};

const eventsFromTurn = (turn: AgentConversationTurn | undefined): Array<AgentEvent> =>
  turn ? eventsFromConversationTurn(turn) : [];

const artifactsFromEvents = (events: Array<AgentEvent>): Array<AgentArtifact> =>
  events.filter((event) => event.type === 'artifact_ready').map(artifactFromEvent);

// Highest sequence already loaded for a conversation, across all turns. Used to
// subscribe for incremental catch-up instead of replaying from the beginning.
export const conversationHistoryLastSequence = (
  history: AgentConversationHistory | undefined,
): number | undefined => lastEventSequence((history?.turns ?? []).flatMap(eventsFromTurn));

const collectText = (value: unknown, output: Array<string>) => {
  if (!value) return;
  if (typeof value === 'string') {
    output.push(value);
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((entry) => {
      collectText(entry, output);
    });
    return;
  }
  if (typeof value !== 'object') return;

  const record = value as Record<string, unknown>;
  // Pydantic request messages contain model-only user-prompt content, including
  // extracted attachment text. Only response trees belong in assistant output.
  if (record.kind === 'request') return;
  const isAssistantText =
    record.type === 'text' ||
    record.type === 'ai' ||
    record.part_kind === 'text' ||
    record.role === 'assistant';
  if (isAssistantText && typeof record.content === 'string') {
    output.push(record.content);
  }
  if (record.parts) collectText(record.parts, output);
  if (record.content && typeof record.content !== 'string') collectText(record.content, output);
};

const collectPlotJson = (value: unknown, output: Array<unknown>) => {
  if (!value) return;
  if (Array.isArray(value)) {
    value.forEach((entry) => {
      collectPlotJson(entry, output);
    });
    return;
  }
  if (typeof value !== 'object') return;

  const record = value as Record<string, unknown>;
  if (record.plot_json) {
    output.push(record.plot_json);
  }
  if (record.parts) collectPlotJson(record.parts, output);
  if (record.content) collectPlotJson(record.content, output);
};

const isPlotJson = (value: unknown): value is AgentMessage['plotJson'] => {
  if (!value || typeof value !== 'object') return false;
  return Array.isArray((value as Record<string, unknown>).data);
};
